import { Box, Grid, Typography } from '@mui/material';
import { connect } from 'react-redux';
import { BasicTable, LargeButton, MarkComplete } from '../../components';
import {
	DARK_MODE,
	DEFAULT_COLUMNS,
	ERROR_MESSAGE,
	COLUMNS_TO_HIDE_FOR_CHASSIS_LINE,
	CHASIS_LINE_IDS,
	OPTIONAL_COLUMNS,
	PLATE_PUNCHING_FILE_CREATION_ERROR,
} from '../../constants';
import { pxToRem, responsiveFontSizes } from '../../utils/getFontValue';
import { useMessageContextManager } from '../../components/MessageOverlay/MessageOverlay';
import { platePunchingInitiate } from '../../redux/actionCreators/platePunching';
import { useEffect, useMemo, useState } from 'react';
import { markComplete } from '../../redux/actionCreators/printBarcode';
import { useRedirectToNextScreen } from '../../hooks';
import { useOutletContext } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';

const folder_name = 'IPMS';
const file_name = 'punching1';
const Error_log_folder_name = 'assembly-error-log';
const Error_log_file_name = 'engraving.txt';

const ROW_HEIGHT = 38;

const classNames = {
	paper: {
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
	},
	tableRow: {
		verticalAlign: 'top',
	},
	tableContainer: {
		height: 3 * ROW_HEIGHT,
	},
	tableContentContainer: {
		flex: 1,
		backgroundColor: 'background.default',
		borderBottomLeftRadius: '5px',
		borderBottomRightRadius: '5px',
		padding: '20px',
	},
	heading: {
		color: '#FFA222',
		fontSize: pxToRem(12),
		...responsiveFontSizes({ sm: 12, md: 14, lg: 18 }),
	},
	text: {
		letterSpacing: 0.5,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		fontWeight: 'bold',
		fontSize: pxToRem(16),
		...responsiveFontSizes({ sm: 16, md: 18, lg: 24 }),
	},
	enterSerialNumber: {
		color: '#3070db',
		paddingLeft: 1,
		fontWeight: 700,
		lineHeight: 1.5,
		fontSize: pxToRem(14),
		...responsiveFontSizes({ sm: 14, md: 16, lg: 24 }),
		'&:hover': {
			backgroundColor: 'transparent',
		},
	},
	textInput: {
		borderRadius: 2,
		background: ({ palette }) =>
			palette.mode === DARK_MODE ? 'white' : palette.background.paper,
		paddingLeft: 2,
	},
	buttonStyles: {
		marginRight: 0,
		px: 4,
		background: '#82C1A7',
		borderRadius: 1,
		color: 'white',
		width: 70,

		'&.Mui-disabled': {
			color: 'rgba(0, 0, 0, 0.26)',
			backgroundColor: '#d8d8d8',
		},
	},
	model: {
		position: 'absolute',
		background: '#000000e0',
		top: 0,
		height: '100vh',
		left: 0,
		width: '100vw',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
	},
	successLogo: {
		height: '65%',
		width: '21%',
		color: '#82C1A7',
	},
	cell: {
		borderBottom: 'none',
		paddingBottom: 0,
	},
};

const mapStateToProps = (state) => {
	return {
		partValidation: state.partValidation,
		currentLineItem: state.station.currentLineItem,
		user: state.user,
	};
};

const mapDispatchToProps = (dispatch) => ({
	platePunchingInitiate: (sequenceNumber, successCallback, errorCallback) =>
		dispatch(
			platePunchingInitiate(sequenceNumber, successCallback, errorCallback),
		),
	markComplete: (successCallback, errorCallback, sequenceNumber) =>
		dispatch(markComplete(successCallback, errorCallback, sequenceNumber)),
});

export const PlatePunchingStart = ({
	platePunchingInitiate,
	currentLineItem,
	markComplete,
	user,
}) => {
	const [currentAgsnData, setCurrentAgsnData] = useState({});
	const { showMessageOverlay } = useMessageContextManager();
	const [showMarkCompleteMOdel, setShowMarkCompleteMOdel] = useState(false);
	const redirect = useRedirectToNextScreen();
	const { handleNext } = useOutletContext();

	useEffect(() => {
		if (Object.keys(currentLineItem).length > 0) {
			setCurrentAgsnData({
				...currentLineItem,
				id: currentLineItem.sequenceNumber,
				readyForDropSequenceNumber: currentLineItem.metaData.readyForDropSequenceNumber,
				vcDescription: currentLineItem.metaData.vcDescription,
				vcSequenceNumber: currentLineItem.metaData.vcSequenceNumber,
				vcPartNumber: currentLineItem.metaData.vcPartNumber,
				shiftLabel: currentLineItem.metaData.shiftLabel,
				vcNumber: currentLineItem.metaData.vcNumber,
			});
		}
	}, [currentLineItem]);

	const PlatePunchingFileSuccess = ({ ctaMarkComplete }) => {
		return (
			<div style={classNames.model}>
				<Box sx={{ display: 'flex', alignItems: 'center', width: '45%' }}>
					<CheckIcon style={classNames.successLogo} />
					<Box>
						<Typography variant="h3" sx={{ color: '#82C1A7' }}>
							Plate punching file created.
						</Typography>
						<Typography variant="h3" sx={{ color: '#82C1A7' }}>
							Complete the operation to proceed.
						</Typography>
					</Box>
				</Box>

				<Box sx={{ marginTop: 2 }}>
					<MarkComplete handleButtonClick={ctaMarkComplete} />
				</Box>
			</div>
		);
	};

	const ctaMarkComplete = () => {
		markComplete(
			() => {
				setShowMarkCompleteMOdel(false);
				redirect(() => handleNext());
			},
			(error) => {
				showMessageOverlay({
					contextText: ERROR_MESSAGE.MARK_COMPLETE_ERROR_MESSAGE,
					info: error?.response?.data?.message,
					error: true,
				});
				// logError(`Mark complete ${error}`);
			},
			currentLineItem.sequenceNumber,
		);
	};

	const filteredOriginalDefaultColumns = useMemo(() => {
		if (CHASIS_LINE_IDS.includes(user?.lineId)) {
			return DEFAULT_COLUMNS.filter((res) => {
				return !COLUMNS_TO_HIDE_FOR_CHASSIS_LINE.includes(res.field);
			});
		}
		return DEFAULT_COLUMNS; // Return the array directly
	}, [DEFAULT_COLUMNS, user?.lineId]);

	const handleInitialPlatePunch = () => {
		platePunchingInitiate(
			currentAgsnData.sequenceNumber,
			(response) => {
				if (response) {
					const folderName = encodeURIComponent(folder_name);
					const fileName = `${encodeURIComponent(file_name)}.${encodeURIComponent(response.extension)}`;
					const content = encodeURIComponent(response.value);
					window.location.href = `nirmaan://run?cmd=create_file&folder=${folderName}&file=${fileName}&content=${content}`;
					setShowMarkCompleteMOdel(true);
				}
			},
			(error) => {
				showMessageOverlay({
					contextText: PLATE_PUNCHING_FILE_CREATION_ERROR.ERROR_CONTEXT_TEXT,
					info: error?.response?.data?.message,
					error: true,
				});
				const folderName = encodeURIComponent(Error_log_folder_name);
				const fileName = encodeURIComponent(Error_log_file_name);
				const platePunchError = `Plate Punch Error :- ${JSON.stringify(
					error?.response?.data,
				)}`;
				window.location.href = `nirmaan://run?cmd=create_file&folder=${folderName}&file=${fileName}&content=${platePunchError}`;
				// logError(`Error While getting plate punch file data ${error}`);
			},
		);
	};

	return (
		<>
			<BasicTable
				listId="plate-punching"
				rows={[currentAgsnData]}
				columns={[...filteredOriginalDefaultColumns, ...OPTIONAL_COLUMNS]}
				defaultColumns={[...filteredOriginalDefaultColumns]}
				subtitle="Current AgSN"
				tableStyles={{ paper: classNames.paper }}
			>
				<Box sx={classNames.tableContentContainer}>
					<Grid container direction="column" height="100%">
						<Grid item container flexGrow={1}>
							<Grid item>
								<Typography
									variant="body3"
									sx={{ color: (theme) => theme.palette.text.secondary }}
								>
									Aggregate Serial No.
								</Typography>
								<Grid container direction="row" alignItems="center" spacing={3}>
									<Grid item>
										<Typography variant="h5" color="primary">
											{currentAgsnData.serialNumber}
										</Typography>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
						<Grid item container justifyContent="end" alignItems="end">
							<LargeButton
								backgroundColor="#82C1A7"
								name="INITIATE PLATE PUNCH"
								handleButtonClick={handleInitialPlatePunch}
							/>
						</Grid>
					</Grid>
				</Box>
			</BasicTable>
			{showMarkCompleteMOdel && (
				<PlatePunchingFileSuccess ctaMarkComplete={ctaMarkComplete} />
			)}
		</>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(PlatePunchingStart);
